// Per-card liquidity scoring for the Palworld TCG Price Index.
//
// A price is only actionable if copies actually change hands near it. Liquidity
// converts the depth data already stored behind each price row — live listing
// count, how many marketplaces agree, the low/high spread and
// how many days of the window are marketplace-backed — into a single 0–100
// score with a plain-language tier, so a $900 quote built on two listings is
// never presented with the same authority as one built on forty.
import type { PriceRow } from "./prices-shared";

export type LiquidityTier = "deep" | "active" | "thin" | "illiquid";

export interface Liquidity {
  /** 0–100. Higher means easier to buy or sell near the quoted value. */
  score: number;
  tier: LiquidityTier;
  label: string;
  /** One-sentence explanation shown in the UI and in the methodology copy. */
  detail: string;
  /** Live listings behind the quote. */
  depth: number;
  /** Recent completed TCGplayer sales behind the quote. */
  sales: number;
  /** Marketplaces that contributed a trusted quote. */
  sources: number;
  /** Low-to-high asking spread as a percentage of the market value. */
  spreadPct: number;
  /** Days of the 30-day window backed by stored marketplace snapshots. */
  recordedDays: number;
}

export const LIQUIDITY_TIERS: { tier: LiquidityTier; label: string; meaning: string }[] = [
  { tier: "deep", label: "Deep", meaning: "Many live listings plus recent sales across marketplaces — the quote is dependable." },
  { tier: "active", label: "Active", meaning: "Enough listings and sales to trade near the quote, with some day-to-day noise." },
  { tier: "thin", label: "Thin", meaning: "Few validated listings — the median can sit well away from real trade prices." },
  { tier: "illiquid", label: "Illiquid", meaning: "No validated marketplace activity; the value is the modelled rarity baseline." },
];

function clamp(n: number, lo = 0, hi = 100): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Liquidity for one price row. Pure function of the row, so the index, the
 * price chart and the card pages all report the same number.
 */
export function cardLiquidity(row: PriceRow): Liquidity {
  const listings = row.listings ?? 0;
  const sales = row.tcgSoldCount ?? 0;
  const depth = listings + sales;
  const sourceSet = new Set(row.liveSources ?? []);
  if (sales > 0) sourceSet.add("TCGplayer completed sales");
  const sources = sourceSet.size;
  const spreadPct =
    row.market > 0 && row.high > 0 && row.low > 0
      ? Math.max(0, Math.round(((row.high - row.low) / row.market) * 100))
      : 0;
  const recordedDays = row.recordedDays ?? 0;

  if (!sources || depth === 0) {
    return {
      score: 0,
      tier: "illiquid",
      label: "Illiquid",
      detail:
        "No validated marketplace listing or sale in the current window, so this value is the modelled Pal Stars Index rarity baseline rather than a traded price.",
      depth,
      sales,
      sources,
      spreadPct,
      recordedDays,
    };
  }

  // Depth is the dominant term (a market with copies for sale is tradeable),
  // multiple marketplaces guard against a single storefront, recorded history shows persistence, and
  // a wide bid-ask spread subtracts because it means no agreed price exists.
  const depthScore = clamp(Math.log10(1 + depth) * 34, 0, 45);
  const sourceScore = clamp((sources - 1) * 9, 0, 15);
  const historyScore = clamp((recordedDays / 30) * 15, 0, 15);
  const spreadPenalty = clamp(Math.max(0, spreadPct - 40) / 4, 0, 20);

  const score = Math.round(clamp(depthScore + sourceScore + historyScore - spreadPenalty));
  const tier: LiquidityTier = score >= 65 ? "deep" : score >= 35 ? "active" : "thin";
  const meta = LIQUIDITY_TIERS.find((t) => t.tier === tier)!;

  const detail =
    `${meta.meaning} ${listings} validated listing${listings === 1 ? "" : "s"} and ${sales} completed sale${sales === 1 ? "" : "s"} ` +
    `across ${sources} marketplace${sources === 1 ? "" : "s"}, with a ${spreadPct}% low-to-high spread and ` +
    `${recordedDays} of the last 30 days marketplace-backed.`;

  return { score, tier, label: meta.label, detail, depth, sales, sources, spreadPct, recordedDays };
}

/** Tailwind classes per tier. Presentation helper kept beside the scoring. */
export function liquidityTone(tier: LiquidityTier): string {
  return tier === "deep"
    ? "bg-emerald-400/20 text-emerald-100"
    : tier === "active"
      ? "bg-sky-400/20 text-sky-100"
      : tier === "thin"
        ? "bg-amber-400/20 text-amber-100"
        : "bg-white/10 text-white/60";
}
